import { BackButton } from 'src/shared/design-system/BackButton';
import { ZIndexes } from 'src/shared/ZIndexes';
import styled from 'styled-components';
import { ifProp } from 'web-common/src/shared/styled-utils/ifProp';
import { Colors, Font, ScreenWidth, Spacing } from 'web-common/src/shared/styles';
import { MultiCartTotals } from './MultiCartTotals';

export const HeaderContainer = styled.div`
  z-index: ${ZIndexes.StickyFooter};
  position: sticky;
  top: 0;

  @media (max-width: ${ScreenWidth.SMALL}) {
    ${MultiCartTotals} {
      display: none;
    }
  }
`;

export const CloseContainer = styled.button`
  margin-left: -${Spacing.HALF};
  padding: ${Spacing.QUARTER} ${Spacing.HALF};
  display: flex;
  border: none;
  background: none;

  @media (max-width: ${ScreenWidth.SMALL}) {
    display: none;
  }
`;

export const BackButtonContainer = styled(BackButton)`
  margin-right: ${Spacing.SINGLE};
  display: none;
  border: 1px solid ${Colors.GRAY_LITE_10};
  border-radius: 100%;
  width: 24px;
  height: 24px;

  @media (max-width: ${ScreenWidth.SMALL}) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

export const TopContainer = styled.div<{ hasItems: boolean }>`
  background: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 ${Spacing.SINGLE_HALF};
  ${(props) =>
    props.hasItems
      ? `padding-top: ${Spacing.SINGLE_QUARTER};`
      : `height: 48px; border-bottom: 1px solid ${Colors.GRAY_LITE_20};`};

  @media (max-width: ${ScreenWidth.SMALL}) {
    height: 48px;
    border-bottom: 1px solid ${Colors.GRAY_LITE_20};
    padding: 0px ${Spacing.SINGLE};
  }
`;

export const YourCartTextContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const YourCart = styled.div`
  font-family: ${Font.Family.SANS};
  font-weight: ${Font.Weight.BOLD};
  font-size: ${Font.Size.XL};
  margin-top: 2px;

  @media (max-width: ${ScreenWidth.SMALL}) {
    font-weight: ${Font.Weight.NORMAL};
    font-size: ${Font.Size.S};
  }
`;

export const DeliveryDateContainer = styled.div<{ floatForward: boolean }>`
  display: flex;
  align-items: baseline;
  padding: ${Spacing.QUARTER} ${Spacing.SINGLE_HALF} ${Spacing.SINGLE};
  background: white;
  border-bottom: 1px solid ${Colors.GRAY_LITE_20};

  @media (max-width: ${ScreenWidth.SMALL}) {
    padding: 10px 0 10px 16px;
    box-shadow: ${ifProp('floatForward', `0px 2px 4px -1px ${Colors.GRAY_LITE_10}`, 'unset')};
    border-bottom: none;
  }

  @media (min-width: ${ScreenWidth.SMALL}) {
    flex-direction: column;
  }
`;

export const DeliveryDate = styled.div`
  font-family: ${Font.Family.SANS};
  font-weight: ${Font.Weight.SEMI_BOLD};
  font-size: ${Font.Size.S};
  line-height: 1;
`;

export const EditDeliveryDateButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  display: flex;
  align-items: flex-start;
`;

export const CalendarIconSvg = styled.svg`
  margin-left: 6px;
  height: 16px;
  width: 16px;
  padding-bottom: 2px;
`;

export const CalendarIcon = () => (
  <CalendarIconSvg viewBox='0 0 16 16' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <path
      d='M6.17003 3.15878H8.43832C8.7145 3.15878 8.93745 2.92681 8.93745 2.63945C8.93745 2.35208 8.7145 2.12012 8.43832 2.12012H6.17003C5.89384 2.12012 5.6709 2.35208 5.6709 2.63945C5.6709 2.92681 5.89384 3.15878 6.17003 3.15878Z'
      fill='currentColor'
    />
    <path
      d='M15.7826 12.0877C13.897 10.7525 13.1084 4.78939 12.9631 2.60358C12.9442 2.31737 12.7068 2.10387 12.4306 2.12118C12.1556 2.1408 11.9481 2.38893 11.967 2.67513C11.9725 2.75938 12.109 4.76284 12.5571 7.01905C13.0052 9.27294 13.5975 10.9394 14.324 11.9977H4.16055C2.15182 10.0889 1.30329 4.88287 1.07147 3.15292H2.1496C2.42579 3.15292 2.64873 2.92095 2.64873 2.63359C2.64873 2.34623 2.42579 2.11426 2.1496 2.11426H0.499133C0.222946 2.11426 0 2.34623 0 2.63359V15.4818C0 15.7692 0.222946 16.0012 0.499133 16.0012H12.4584C12.7346 16.0012 12.9575 15.7692 12.9575 15.4818V13.0375H15.5009C15.7205 13.0375 15.9135 12.8886 15.9778 12.6717C16.0421 12.4536 15.9623 12.2181 15.7815 12.09L15.7826 12.0877ZM11.9592 14.9613H0.998267V7.81997C1.56173 9.80381 2.41137 11.85 3.65698 12.9175C3.74683 12.9937 3.85886 13.0364 3.97421 13.0364H11.9592V14.9613Z'
      fill='currentColor'
    />
    <path
      d='M1.96597 1.69417C2.20555 1.83497 2.51058 1.74726 2.6459 1.49683C2.8023 1.20946 3.03855 1.03751 3.27814 1.03751C3.71737 1.03751 4.08895 1.588 4.08895 2.2412C4.08895 2.73399 3.87599 3.18754 3.55876 3.36757C3.31696 3.5049 3.22822 3.81997 3.36022 4.07271C3.45117 4.24582 3.62198 4.34276 3.79835 4.34276C3.87932 4.34276 3.9614 4.32199 4.03682 4.27928C4.6746 3.91691 5.08722 3.11714 5.08722 2.2412C5.08611 1.00519 4.27529 0 3.27814 0C2.67363 0 2.11238 0.369302 1.77741 0.986728C1.64209 1.23601 1.72639 1.55338 1.96708 1.69417H1.96597Z'
      fill='currentColor'
    />
    <path
      d='M8.30488 1.69417C8.54557 1.83497 8.84949 1.74726 8.98481 1.49683C9.06578 1.34911 9.16893 1.22793 9.28429 1.1483C9.38966 1.07559 9.50169 1.03751 9.61704 1.03751C10.0563 1.03751 10.4279 1.588 10.4279 2.2412C10.4279 2.78707 10.165 3.27871 9.80338 3.41143C9.54273 3.50606 9.4063 3.80265 9.49725 4.07386C9.56935 4.28736 9.76235 4.42124 9.96865 4.42124C10.023 4.42124 10.0796 4.412 10.1339 4.39238C10.907 4.11079 11.4261 3.24639 11.4261 2.24235C11.4261 1.00635 10.6153 0 9.61704 0C9.30647 0 8.99923 0.0980958 8.73191 0.283901C8.48678 0.453549 8.27382 0.697057 8.11631 0.986728C7.98099 1.23601 8.06529 1.55338 8.30488 1.69417Z'
      fill='currentColor'
    />
    <path
      d='M7.0358 9.69183C7.12564 9.76799 7.23767 9.80954 7.35192 9.80954C7.37743 9.80954 7.40183 9.80723 7.42734 9.80377C7.5671 9.78184 7.69133 9.69875 7.76897 9.57526L9.7411 6.45582C9.89306 6.21577 9.82873 5.89379 9.59802 5.73683C9.36731 5.57873 9.05785 5.64566 8.907 5.88571L7.23878 8.52391L6.19947 7.63989C5.98651 7.4587 5.6715 7.49102 5.49736 7.71375C5.32322 7.93649 5.35428 8.26309 5.56835 8.44428L7.0358 9.69298V9.69183Z'
      fill='currentColor'
    />
  </CalendarIconSvg>
);
